import { FaPhoneAlt, FaStar, FaCircle } from "react-icons/fa";
import { FiSearch, FiMenu, FiX } from "react-icons/fi";
import { MdTrendingUp } from "react-icons/md";
import { GiGraduateCap } from "react-icons/gi";
import { FaGraduationCap } from "react-icons/fa";
import logo from "../asset/logo.png";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { IoPerson } from "react-icons/io5";
import SummaryApi from "../common";
import { useAuth } from "../helpers/AuthContext";
import { DownOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import logowhite from "../asset/logowhite.png";

export default function Navbar() {
  // UI State
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [open, setOpen] = useState(false); // Login dropdown

  // Search Logic State
  const [searchTerm, setSearchTerm] = useState("");
  const [allCourses, setAllCourses] = useState([]); // Stores API data
  const [filteredCourses, setFilteredCourses] = useState([]); // Stores search matches
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Hooks
  const navigate = useNavigate();
  const { studentId, studentName, setStudentName, logout } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;
  
  // REFS
  const searchRef = useRef(null); // Desktop Search Ref
  const mobileSearchRef = useRef(null); // Mobile Search Ref (ADDED THIS)

  // Helper to check active link
  const isActive = (path) => currentPath === path;

  // 1. Close menus when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
    setOpen(false);
    setShowSuggestions(false);
  }, [location.pathname]);

  // 2. Fetch Student Name (if logged in but name missing)
  useEffect(() => {
    if (studentId && !studentName) {
      fetch(SummaryApi.getStudentById.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_id: studentId }),
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status && data.data?.first_name) {
            setStudentName(data.data.first_name);
          } else {
            console.error("Failed to fetch student name");
            logout();
          }
        })
        .catch((err) => {
          console.error("Error fetching student name:", err);
          logout();
        });
    }
  }, [studentId, studentName, setStudentName, logout]);

  // 3. Fetch All Active Courses for Search
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch(SummaryApi.getActiveCourses.url);
        const data = await response.json();

        // Handle different API response structures (array directly or data.data)
        if (Array.isArray(data)) {
          setAllCourses(data);
        } else if (data.data && Array.isArray(data.data)) {
          setAllCourses(data.data);
        } else {
          setAllCourses([]);
        }
      } catch (error) {
        console.error("Error fetching courses for search:", error);
      }
    };
    fetchCourses();
  }, []);

  // 4. Handle Typing in Search Bar
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchTerm(query);

    if (query.trim().length > 0) {
      // Filter logic: Check if course_title contains the query (case insensitive)
      const filtered = allCourses.filter((course) =>
        course.course_title.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredCourses(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  // 5. Handle Clicking a Suggestion
  const handleSuggestionClick = (courseId) => {
    if (!courseId) return;

    // Convert ID to string and encode to Base64
    const encodedId = btoa(courseId.toString());

    // Navigate to /all-courses/{encodedId}
    navigate(`/all-courses/${encodedId}`);

    // Reset UI state
    setShowSuggestions(false);
    setSearchTerm("");
    setIsMobileMenuOpen(false);
  };

  // 6. Handle Pressing Enter (General Search)
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/all-courses?search=${encodeURIComponent(searchTerm)}`);
      setShowSuggestions(false);
      setIsMobileMenuOpen(false);
    }
  };

  // 7. Click Outside to Close Suggestions (UPDATED)
  useEffect(() => {
    function handleClickOutside(event) {
      // Check if click is inside Desktop Ref
      const isInsideDesktop = searchRef.current && searchRef.current.contains(event.target);
      // Check if click is inside Mobile Ref
      const isInsideMobile = mobileSearchRef.current && mobileSearchRef.current.contains(event.target);

      // If click is outside BOTH, close suggestions
      if (!isInsideDesktop && !isInsideMobile) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchRef, mobileSearchRef]);

  // Navigation Helpers
 const handleLogoClick = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
  navigate("/");
};

  const handleLogout = () => {
    logout();
    navigate("/student-login");
  };

  return (
    <>
      <style jsx>{`
        @media (max-width: 1023px) {
          .mobile-menu-link {
            position: relative;
            transition: background-color 0.2s, color 0.2s;
          }
          .mobile-menu-link:hover,
          .mobile-menu-link:active,
          .mobile-menu-link.mobile-active {
            background-color: #163165 !important;
            color: white !important;
          }
        }
        /* Custom Scrollbar for Search Dropdown */
        .search-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .search-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .search-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .search-scroll::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>

      <div
        className="w-full font-sans dark:bg-gray-900"
        style={{ zIndex: 1000, top: 0, position: "sticky" }}
      >
        <div className="bg-white dark:bg-gray-900 flex justify-between items-center px-4 py-4 shadow-sm relative">
          {/* Logo */}
          <div className="w-auto leading-tight">
            <button onClick={handleLogoClick} className="focus:outline-none">
              <img
              
                src={logo}
                alt="Logo"
                className="w-32 sm:w-36 cursor-pointer block dark:hidden"
              />
              <img
                src={logowhite}
                alt="Logo"
                className="w-32 sm:w-36 cursor-pointer hidden dark:block"
              />
            </button>
          </div>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-6 text-[#163165] dark:text-white font-medium text-[16px] relative">
            {/* Categories Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1 px-3 py-1 rounded-md ${
                  currentPath === "/trendy-courses" ||
                  currentPath === "/carrer-courses" ||
                  currentPath === "/degree-courses"
                    ? "bg-[#163165] dark:bg-blue-700 text-white"
                    : "hover:text-blue-700 dark:hover:text-blue-400"
                }`}
              >
                Categories
                <svg
                  className={`w-4 h-4 transition-transform ${
                    isDropdownOpen ? "rotate-180" : "rotate-0"
                  }`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg z-50 w-48">
                  <div className="py-1">
                    <a
                      href="/trendy-courses"
                      className="block px-4 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 cursor-pointer"
                    >
                      <MdTrendingUp className="text-[#163165] dark:text-blue-400 text-lg" />
                      <span className="text-sm dark:text-white">
                        Trending Courses
                      </span>
                    </a>
                    <a
                      href="/carrer-courses"
                      className="block px-4 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 cursor-pointer"
                    >
                      <GiGraduateCap className="text-[#163165] dark:text-blue-400 text-lg" />
                      <span className="text-sm dark:text-white">
                        Career Related Program
                      </span>
                    </a>
                    {/* <a
                      href="/degree-courses"
                      className="block px-4 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 cursor-pointer"
                    >
                      <FaGraduationCap className="text-[#163165] dark:text-blue-400 text-lg" />
                      <span className="text-sm dark:text-white">
                        Degree Courses
                      </span>
                    </a> */}
                  </div>
                </div>
              )}
            </div>

            <a
              href="/all-courses"
              className={`px-3 py-1 rounded-md whitespace-nowrap ${
                isActive("/all-courses")
                  ? "bg-[#163165] dark:bg-blue-700 text-white"
                  : "hover:text-blue-700 dark:hover:text-blue-400"
              }`}
            >
              All Courses
            </a>
            <a
              href="/all-instructors"
              className={`px-3 py-1 rounded-md whitespace-nowrap ${
                isActive("/all-instructors")
                  ? "bg-[#163165] dark:bg-blue-700 text-white"
                  : "hover:text-blue-700 dark:hover:text-blue-400"
              }`}
            >
              Instructors
            </a>
            <a
              href="/Certificate-Verification"
              className={`px-3 py-1 rounded-md whitespace-nowrap ${
                isActive("/Certificate-Verification")
                  ? "bg-[#163165] dark:bg-blue-700 text-white"
                  : "hover:text-blue-700 dark:hover:text-blue-400"
              }`}
            >
              Verify Certificate
            </a>
            <a
              href="/resourses"
              className={`px-3 py-1 rounded-md whitespace-nowrap ${
                isActive("/resourses")
                  ? "bg-[#163165] dark:bg-blue-700 text-white"
                  : "hover:text-blue-700 dark:hover:text-blue-400"
              }`}
            >
              Resources
            </a>
          </div>

          {/* Desktop Right Side: Search + Auth */}
          <div className="hidden lg:flex items-center gap-4">
            {/* --- Desktop Search Bar --- */}
            <div className="relative" ref={searchRef}>
              <form
                onSubmit={handleSearchSubmit}
                className="flex items-center border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 hover:border-blue-500 transition-colors bg-white dark:bg-gray-800"
              >
                <FiSearch className="text-gray-500 dark:text-gray-400" />
                <input
                  type="text"
                  placeholder="Search Courses..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="ml-2 outline-none bg-transparent text-sm text-gray-700 dark:text-white w-40 xl:w-56 placeholder-gray-500"
                  autoComplete="off"
                />
              </form>

              {/* Suggestions Dropdown */}
              {showSuggestions && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 max-h-64 overflow-y-auto search-scroll">
                  {filteredCourses.length > 0 ? (
                    filteredCourses.map((course) => (
                      <div
                        key={course.course_id}
                        onClick={() => handleSuggestionClick(course.course_id)}
                        className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-3 border-b border-gray-100 dark:border-gray-700 last:border-none"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-[#163165] dark:text-white">
                            {course.course_title}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                      No course found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* --- Auth Buttons --- */}
            {studentName ? (
              <div className="relative inline-block text-left">
                <button
                  onClick={() => setOpen(!open)}
                  className="flex items-center gap-2 border border-[#163165] dark:border-blue-600 text-[#163165] dark:text-white px-4 py-1 rounded-md hover:bg-[#163165] dark:hover:bg-blue-700 hover:text-white transition text-base"
                >
                  <div className="w-8 h-8 rounded-full bg-[#163165] dark:bg-blue-600 text-white flex items-center justify-center">
                    <IoPerson size={18} />
                  </div>
                  <span className="font-semibold whitespace-nowrap">
                    {studentName}
                  </span>
                  <span className="text-xs">
                    <DownOutlined />
                  </span>
                </button>
                {open && (
                  <div className="absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black dark:ring-gray-700 ring-opacity-5 z-10">
                    <a
                      href="/student-panel/student-dashboard"
                      className="block p-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Dashboard
                    </a>
                    <a
                      href="/student-panel/profile"
                      className="block p-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      Profile
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="relative inline-block text-left">
                  <button
                    onClick={() => setOpen(!open)}
                    className="border border-[#163165] dark:border-blue-600 text-[#163165] dark:text-white px-4 py-1 rounded-md hover:bg-[#163165] dark:hover:bg-blue-700 hover:text-white transition text-base whitespace-nowrap"
                  >
                    Log In
                  </button>
                  {open && (
                    <div className="absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black dark:ring-gray-700 ring-opacity-5 z-10">
                      <a
                        href="/student-login"
                        className="flex items-center px-4 py-1 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border-b-[2px] dark:border-gray-600"
                      >
                        🎓 Student
                      </a>
                      <a
                        href="/instructor-login"
                        className="flex items-center px-4 py-1 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        🎓 Instructor
                      </a>
                    </div>
                  )}
                </div>
                <a
                  href="/student-create"
                  className="bg-[#1C3664] hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-700 text-white px-5 py-1 rounded-md transition text-base whitespace-nowrap"
                >
                  Sign Up
                </a>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle Button */}
          <button
            className="lg:hidden text-2xl text-[#163165] dark:text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>

        {/* --- Mobile Menu Drawer --- */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white dark:bg-gray-800 shadow-md px-4 py-4 space-y-4 absolute top-full left-0 right-0 z-50">
            {/* Mobile Search Bar WITH REF ADDED */}
            <div className="relative w-full" ref={mobileSearchRef}>
              <form
                onSubmit={handleSearchSubmit}
                className="flex items-center w-full bg-gray-100 dark:bg-gray-700 rounded-md px-3 py-3"
              >
                <FiSearch className="text-[#163165] dark:text-white" />
                <input
                  type="text"
                  placeholder="Search Courses..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full bg-transparent outline-none ml-2 text-gray-700 dark:text-white text-sm"
                />
              </form>

              {/* Mobile Suggestions */}
              {showSuggestions && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50 max-h-56 overflow-y-auto">
                  {filteredCourses.length > 0 ? (
                    filteredCourses.map((course) => (
                      <div
                        key={course.course_id}
                        onClick={() => handleSuggestionClick(course.course_id)}
                        className="px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center border-b dark:border-gray-700 last:border-none"
                      >
                        <span className="text-sm font-medium text-[#163165] dark:text-white">
                          {course.course_title}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                      No course found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Links */}
            <div>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center justify-between w-full font-medium text-[#163165] dark:text-white text-base"
              >
                <span>Categories</span>
                <svg
                  className={`w-4 h-4 transition-transform ${
                    isDropdownOpen ? "rotate-180" : "rotate-0"
                  }`}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {isDropdownOpen && (
                <div className="pl-4 mt-2 space-y-2">
                  <a
                    href="/trendy-courses"
                    className="mobile-menu-link flex items-center gap-2 dark:text-white text-sm py-1 px-3 rounded-md"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <MdTrendingUp className="text-[#163165] dark:text-blue-400" />
                    <span>Trending Courses</span>
                  </a>
                  <a
                    href="/carrer-courses"
                    className="mobile-menu-link flex items-center gap-2 dark:text-white text-sm py-1 px-3 rounded-md"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <GiGraduateCap className="text-[#163165] dark:text-blue-400" />
                    <span>Career Related Program</span>
                  </a>
                  {/* <a
                    href="/degree-courses"
                    className="mobile-menu-link flex items-center gap-2 dark:text-white text-sm py-1 px-3 rounded-md"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <FaGraduationCap className="text-[#163165] dark:text-blue-400" />
                    <span>Degree Courses</span>
                  </a> */}
                </div>
              )}
            </div>

            <a
              href="/all-courses"
              className={`mobile-menu-link block text-[#163165] dark:text-white text-base py-1 px-3 rounded-md ${
                isActive("/all-courses") ? "mobile-active" : ""
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              All Courses
            </a>
            <a
              href="/all-instructors"
              className={`mobile-menu-link block text-[#163165] dark:text-white text-base py-1 px-3 rounded-md ${
                isActive("/all-instructors") ? "mobile-active" : ""
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Instructors
            </a>
            <a
              href="/Certificate-Verification"
              className={`mobile-menu-link block text-[#163165] dark:text-white text-base py-1 px-3 rounded-md ${
                isActive("/Certificate-Verification") ? "mobile-active" : ""
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Verify Certificate
            </a>
            <a
              href="/resourses"
              className={`mobile-menu-link block text-[#163165] dark:text-white text-base py-1 px-3 rounded-md ${
                isActive("/resourses") ? "mobile-active" : ""
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Resources
            </a>

            {/* Mobile Auth */}
            {studentName ? (
              <div className="relative inline-block text-left w-full">
                <button
                  onClick={() => setOpen(!open)}
                  className="flex items-center justify-between w-full border border-[#163165] dark:border-blue-600 text-[#163165] dark:text-white p-2 rounded-md hover:bg-[#163165] dark:hover:bg-blue-700 hover:text-white transition text-base"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#163165] dark:bg-blue-600 text-white flex items-center justify-center">
                      <IoPerson size={18} />
                    </div>
                    <span className="font-semibold text-sm">{studentName}</span>
                  </div>
                  <span className="text-[10px]">
                    <DownOutlined />
                  </span>
                </button>

                {open && (
                  <div className="mt-2 w-full rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black dark:ring-gray-700 ring-opacity-5 z-10">
                    <Link
                      to="/student-panel/profile"
                      className="block p-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => {
                        setOpen(false);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Profile
                    </Link>
                    <Link
                      to="/student-panel/student-dashboard"
                      className="block p-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => {
                        setOpen(false);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      Dashboard
                    </Link>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex justify-between gap-3">
                <div className="relative flex-1">
                  <button
                    onClick={() => setOpen(!open)}
                    className="w-full border border-[#163165] dark:border-blue-600 text-[#163165] dark:text-white px-3 py-1 rounded-md hover:bg-[#163165] dark:hover:bg-blue-700 hover:text-white transition text-base"
                  >
                    Log In
                  </button>
                  {open && (
                    <div className="absolute left-0 right-0 mt-2 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black dark:ring-gray-700 ring-opacity-5 z-10">
                      <a
                        href="/student-login"
                        className="flex items-center px-4 py-1 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border-b-[2px] dark:border-gray-600"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        🎓 Student
                      </a>
                      <a
                        href="/instructor-login"
                        className="flex items-center px-4 py-1 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        🎓 Instructor
                      </a>
                    </div>
                  )}
                </div>
                <a
                  href="/student-create"
                  className="flex-1 bg-[#163165] dark:bg-blue-700 text-white px-3 py-1 rounded-md hover:bg-[#1a2b5e] dark:hover:bg-blue-600 transition text-base text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign Up
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}